import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// GTM tracking helper
const trackEvent = (eventName, eventData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });
  }
};

const contactSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(10, 'Contact details are required'),
  company: z.string().min(1, 'Company / Brand is required'),
  has_ecommerce_store: z.string().min(1, 'Please select an option'),
  interest: z.string().min(1, 'Please select your interest'),
  parcel_sizes: z.array(z.string()).min(1, 'Please select at least one parcel size'),
  custom_branding: z.string().min(1, 'Please select an option'),
  max_weight_ok: z.string().min(1, 'Please select an option'),
  store_url: z.string().min(1, 'Please provide your online store URL'),
});

const ecommerceStoreOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const interestOptions = [
  { value: 'delivery', label: 'Deliveries' },
];

const parcelSizeOptions = [
  { value: 'stock_box_1', label: 'Stock Box 1' },
  { value: 'stock_box_2', label: 'Stock Box 2' },
  { value: 'stock_box_3', label: 'Stock Box 3' },
  { value: 'stock_box_4_plus', label: 'Stock Box 4 and up' },
];

const customBrandingOptions = [
  { value: 'yes', label: 'Yes, all good' },
  { value: 'no_open', label: 'No, would you be open to a customer experience focused delivery service?' },
];

export function InlineContactForm({ 
  preselectedInterest = '', 
  title = "Get a Quote",
  subtitle = "Fill in the form below and we'll get back to you within 24 hours.",
  buttonText = null
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [selectedParcelSizes, setSelectedParcelSizes] = useState([]);
  const [hasEcommerceStore, setHasEcommerceStore] = useState('');
  const [hasOversizedParcels, setHasOversizedParcels] = useState(false);
  const [isOverweight, setIsOverweight] = useState(false);
  
  // Use buttonText prop if provided, otherwise use title
  const submitButtonText = buttonText || title;

  // Check if form can be submitted
  const cannotSubmitNoStore = hasEcommerceStore === 'no';
  const cannotSubmitOversized = hasOversizedParcels;
  const cannotSubmitOverweight = isOverweight;
  const formBlocked = cannotSubmitNoStore || cannotSubmitOversized || cannotSubmitOverweight;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      interest: preselectedInterest,
      has_ecommerce_store: '',
      parcel_sizes: [],
      custom_branding: '',
      max_weight_ok: '',
    },
  });

  const handleEcommerceStoreChange = (value) => {
    setHasEcommerceStore(value);
    setValue('has_ecommerce_store', value);
  };

  const handleParcelSizeChange = (value, checked) => {
    const newSizes = checked 
      ? [...selectedParcelSizes, value]
      : selectedParcelSizes.filter(s => s !== value);
    setSelectedParcelSizes(newSizes);
    setValue('parcel_sizes', newSizes);
    
    // Check if "Stock Box 4 and up" is selected
    setHasOversizedParcels(newSizes.includes('stock_box_4_plus'));
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    trackEvent('inline_contact_form_submit', {
      interest: data.interest,
      has_ecommerce_store: data.has_ecommerce_store,
      custom_branding: data.custom_branding,
      source_page: preselectedInterest
    });

    try {
      await axios.post(`${API}/contact`, data);
      setSubmitStatus('success');
      reset();
      setSelectedParcelSizes([]);
      setHasEcommerceStore('');
      trackEvent('inline_contact_form_success');
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      trackEvent('inline_contact_form_error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gray-50" id="contact-form">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Contact Us</span>
          <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 md:p-10 shadow-soft border border-gray-100"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Row 1: Name and Email */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="inline-name" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Name *
                </Label>
                <Input
                  id="inline-name"
                  data-testid="inline-name-input"
                  placeholder="Your name"
                  className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="inline-email" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Email *
                </Label>
                <Input
                  id="inline-email"
                  type="email"
                  data-testid="inline-email-input"
                  placeholder="your@email.com"
                  className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
            </div>

            {/* Row 2: Contact Details and Company */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="inline-phone" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Contact Details *
                </Label>
                <Input
                  id="inline-phone"
                  data-testid="inline-phone-input"
                  placeholder="+27..."
                  className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                  {...register('phone')}
                />
                {errors.phone && (
                  <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="inline-company" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Company / Brand *
                </Label>
                <Input
                  id="inline-company"
                  data-testid="inline-company-input"
                  placeholder="Your business name"
                  className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                  {...register('company')}
                />
                {errors.company && (
                  <p className="text-red-600 text-sm mt-1">{errors.company.message}</p>
                )}
              </div>
            </div>

            {/* Row 3: Ecommerce Store and Interest */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Do you have an ecommerce store? *
                </Label>
                <Select onValueChange={handleEcommerceStoreChange}>
                  <SelectTrigger 
                    data-testid="inline-ecommerce-select"
                    className={`h-12 rounded-xl ${cannotSubmitNoStore ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {ecommerceStoreOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.has_ecommerce_store && (
                  <p className="text-red-600 text-sm mt-1">{errors.has_ecommerce_store.message}</p>
                )}
                {cannotSubmitNoStore && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <p className="text-red-700 text-xs font-medium flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Drop24 services are only available for ecommerce stores.
                    </p>
                  </motion.div>
                )}
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  What are you interested in? *
                </Label>
                <Select 
                  defaultValue={preselectedInterest}
                  onValueChange={(value) => setValue('interest', value)}
                >
                  <SelectTrigger 
                    data-testid="inline-interest-select"
                    className="h-12 rounded-xl border-gray-200"
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {interestOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.interest && (
                  <p className="text-red-600 text-sm mt-1">{errors.interest.message}</p>
                )}
              </div>
            </div>

            {/* Parcel Sizes */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">
                What size parcels do you send? (Select all that apply) *
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {parcelSizeOptions.map((option) => (
                  <div 
                    key={option.value} 
                    className={`flex items-center space-x-2 p-3 rounded-xl transition-colors ${
                      option.value === 'stock_box_4_plus' && hasOversizedParcels
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <Checkbox
                      id={`inline-${option.value}`}
                      data-testid={`inline-parcel-${option.value}`}
                      checked={selectedParcelSizes.includes(option.value)}
                      onCheckedChange={(checked) => handleParcelSizeChange(option.value, checked)}
                      className="border-gray-300 data-[state=checked]:bg-drop24-red data-[state=checked]:border-drop24-red"
                    />
                    <label
                      htmlFor={`inline-${option.value}`}
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
              {errors.parcel_sizes && (
                <p className="text-red-600 text-sm mt-1">{errors.parcel_sizes.message}</p>
              )}
              {hasOversizedParcels && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-red-700 text-xs font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Parcels larger than Stock Box 3 are outside our service capabilities.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Custom Branding */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Do you prioritise customer experience in your Deliveries? *
              </Label>
              <Select onValueChange={(value) => setValue('custom_branding', value)}>
                <SelectTrigger 
                  data-testid="inline-branding-select"
                  className="h-12 rounded-xl border-gray-200"
                >
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {customBrandingOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.custom_branding && (
                <p className="text-red-600 text-sm mt-1">{errors.custom_branding.message}</p>
              )}
            </div>

            {/* Maximum Weight */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Maximum Weight 3 kilograms? *
              </Label>
              <Select onValueChange={(value) => {
                setValue('max_weight_ok', value);
                setIsOverweight(value === 'no');
              }}>
                <SelectTrigger 
                  data-testid="inline-weight-select"
                  className="h-12 rounded-xl border-gray-200"
                >
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes, under 3kg</SelectItem>
                  <SelectItem value="no">No, over 3kg</SelectItem>
                </SelectContent>
              </Select>
              {errors.max_weight_ok && (
                <p className="text-red-600 text-sm mt-1">{errors.max_weight_ok.message}</p>
              )}
              {isOverweight && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-red-700 text-xs font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Drop24 can only deliver orders under 3 kilograms.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Online Store URL */}
            <div>
              <Label htmlFor="inline-store-url" className="text-sm font-semibold text-gray-700 mb-2 block">
                Please provide us with your online store URL?
              </Label>
              <Input
                id="inline-store-url"
                data-testid="inline-store-url-input"
                placeholder="https://yourstore.co.za"
                className="h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-drop24-red/20 focus:border-drop24-red"
                {...register('store_url')}
              />
              {errors.store_url && (
                <p className="text-red-600 text-sm mt-1">{errors.store_url.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting || formBlocked}
              data-testid="inline-submit-button"
              className={`w-full px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                formBlocked 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-drop24-red text-white hover:bg-[#c4191f] shadow-soft hover:shadow-soft-lg disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : formBlocked ? (
                <>
                  <AlertCircle className="w-5 h-5" />
                  Cannot Submit
                </>
              ) : (
                <>
                  {submitButtonText}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="inline-contact-success"
                className="flex items-center gap-3 p-4 bg-green-50 border border-green-100 text-green-700 rounded-2xl"
              >
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <p>Thank you! Your message has been sent. We'll be in touch shortly.</p>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="inline-contact-error"
                className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 text-red-700 rounded-2xl"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p>Something went wrong. Please try again or contact us directly.</p>
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}

export default InlineContactForm;
